import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { Path, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button, { RemoveButton } from '@/components/Button';
import { Option, StyledSelect } from '@/components/Forms/Dropdown';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import { EditorialQueuePage } from '@/types/resources';
import { api } from '@/utils/api';
import { EditorialFacebookPostType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

import { postTypes } from './NewPostModal';

const Dialog = styled(StyledDialog)`
  width: 460px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Body = styled.div`
  padding: 24px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* height: 400px;
  overflow-y: auto;
  ${scrollBarStyles}; */
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const Switch = styled.div`
  background: ${({ theme }) => theme.colors.white};
`;

const Section = styled.div`
  margin-top: 4px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ThresholdField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.div`
  gap: 12px;
  display: flex;
  align-items: flex-start;
  > div {
    flex: 1;
    min-width: 0;
  }
`;

const StyledRemoveButton = styled(RemoveButton)`
  height: 46px;
  width: 46px;
  border-color: ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
`;

type FormDataT = {
  notifications_settings: {
    empty_queue_notification: {
      enabled: boolean;
      start_time: string;
      end_time: string;
      min_posts: string;
      slack_channel_id: string;
      post_type_thresholds?: Record<number, string>;
    };
  };
};

interface Props {
  onClose: VoidFunction;
  activePage: EditorialQueuePage;
}

const NotificationsSettingsModal: FC<Props> = ({ onClose, activePage }) => {
  const queryClient = useQueryClient();
  const settings = useQueueSettings(activePage);
  const [network] = useNetwork();
  const alerts = useAlerts();

  const emptyQueue = settings?.notificationsSettings?.empty_queue_notification;

  const {
    control,
    reset,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      notifications_settings: {
        empty_queue_notification: {
          enabled: emptyQueue?.enabled ?? false,
          start_time: emptyQueue?.start_time ?? '',
          end_time: emptyQueue?.end_time ?? '',
          min_posts: emptyQueue?.min_posts ?? '1',
          slack_channel_id: emptyQueue?.slack_channel_id ?? '',
          post_type_thresholds: emptyQueue?.post_type_thresholds ?? {},
        },
      },
    },
  });

  // Which post types have a per-type threshold row visible. Seeded from
  // persisted thresholds; values themselves live in the form record.
  const [watchedTypes, setWatchedTypes] = useState<EditorialFacebookPostType[]>(
    () =>
      Object.keys(emptyQueue?.post_type_thresholds ?? {}).map(
        Number,
      ) as EditorialFacebookPostType[],
  );

  const typeOptions: Option<EditorialFacebookPostType>[] = postTypes
    .filter((t) => t.supportedNetworks.includes(network))
    .map((t) => ({ label: t.label, value: t.type }));

  const availableOptions = typeOptions.filter(
    (o) => !watchedTypes.includes(o.value),
  );

  const thresholdName = (type: EditorialFacebookPostType) =>
    `notifications_settings.empty_queue_notification.post_type_thresholds.${type}` as Path<FormDataT>;

  const addTypes = (types: EditorialFacebookPostType[]) => {
    types.forEach((type) => {
      if (!watchedTypes.includes(type)) {
        setValue(thresholdName(type), '1', { shouldDirty: true });
      }
    });
    setWatchedTypes((prev) => Array.from(new Set([...prev, ...types])));
  };

  const removeType = (type: EditorialFacebookPostType) => {
    setValue(thresholdName(type), '', { shouldDirty: true });
    setWatchedTypes((prev) => prev.filter((t) => t !== type));
  };

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      const rawThresholds =
        data.notifications_settings.empty_queue_notification
          .post_type_thresholds ?? {};

      const post_type_thresholds: Record<number, string> = {};
      watchedTypes.forEach((type) => {
        const value = rawThresholds[type];
        if (value != null && `${value}`.trim() !== '') {
          post_type_thresholds[type] = `${value}`;
        }
      });

      const cleanedData: FormDataT = {
        notifications_settings: {
          empty_queue_notification: {
            ...data.notifications_settings.empty_queue_notification,
            post_type_thresholds,
          },
        },
      };

      try {
        await api.post('/v1/editorial/queue/settings', {
          ...cleanedData,
          network,
          page_id: activePage.id,
        });

        await queryClient.invalidateQueries([
          'editorial_distribution_queue_channels',
        ]);

        alerts.success('Settings saved');

        setWatchedTypes(
          Object.keys(post_type_thresholds).map(
            Number,
          ) as EditorialFacebookPostType[],
        );
        reset(cleanedData);
      } catch (err) {
        handleFormErrors(err, setError, alerts, true);
      }
    })();
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Notifications Settings</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <Switch>
            <FormField<FormDataT>
              control={control}
              name={'notifications_settings.empty_queue_notification.enabled'}
              type="switch"
              headline="Notify on slack when distribution queue is getting empty"
            />
          </Switch>
          <FormField<FormDataT>
            control={control}
            label="Start time"
            placeholder="08:00"
            name={'notifications_settings.empty_queue_notification.start_time'}
          />
          <FormField<FormDataT>
            control={control}
            label="End time"
            placeholder="17:00"
            name={'notifications_settings.empty_queue_notification.end_time'}
          />
          <FormField<FormDataT>
            control={control}
            label="Pending posts threshold"
            placeholder="0"
            name={'notifications_settings.empty_queue_notification.min_posts'}
          />
          <FormField<FormDataT>
            control={control}
            label="Slack channel ID"
            placeholder="Add slack channel ID"
            name={
              'notifications_settings.empty_queue_notification.slack_channel_id'
            }
          />
          <Section>
            <Text $type="captionSemibold">
              Per post-type thresholds (optional)
            </Text>
            <StyledSelect
              classNamePrefix="react-select"
              isMulti
              options={availableOptions}
              value={[]}
              placeholder="Select post types to watch"
              onChange={(opts) =>
                addTypes(
                  (opts as Option<EditorialFacebookPostType>[]).map(
                    (o) => o.value,
                  ),
                )
              }
            />
            {watchedTypes.map((type) => (
              <ThresholdField key={type}>
                <Text $type="smallBold">
                  {typeOptions.find((o) => o.value === type)?.label ??
                    `Type ${type}`}
                </Text>
                <Row>
                  <FormField<FormDataT>
                    control={control}
                    type="integer"
                    name={thresholdName(type)}
                  />
                  <StyledRemoveButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeType(type);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18 18 6M6 6l12 12"
                      />
                    </svg>
                  </StyledRemoveButton>
                </Row>
              </ThresholdField>
            ))}
          </Section>
        </Body>
        <Footer>
          <Button
            type="button"
            color="primary"
            disabled={isSubmitting || !isDirty}
            loading={isSubmitting}
            onClick={onSubmit}
          >
            Save
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default NotificationsSettingsModal;
